import { useCallback, useEffect, useMemo, useState } from "react";
import { Image as AntImage, Spin, message } from "antd";
import { Trans } from "react-i18next";
import { t } from "i18next";
import { getUserAssets, type UserAssetRecord } from "@apis/http/api";
import { PathsApiUsersAssetsGetParametersQueryAssetType } from "@apis/generated/openapi";
import { useScrollLoad } from "@hooks/useScrollLoad";
import libraryService from "@apis/libraryService";
import Section from "@components/Section";
import type { Status } from "@models/common";
import { LIBRARY_IMAGE_ASSETS_REFRESH_EVENT } from "@pages/library/events";
import UIImageCard from "./UIImageCard";

interface UIImageListProps {
  enabled: boolean;
}

const PAGE_SIZE = 20;

const UIImageList = ({ enabled }: UIImageListProps) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSrc, setPreviewSrc] = useState("");
  const [deletingId, setDeletingId] = useState<number | string | null>(null);

  const params = useMemo(() => ({}), []);

  const { items, loading, loadingMore, hasMore, sentinelRef, error, refresh } = useScrollLoad<UserAssetRecord>({
    enabled,
    pageSize: PAGE_SIZE,
    params,
    fetcher: async ({ pageNo, pageSize }) => {
      const res = await getUserAssets({
        assetType: PathsApiUsersAssetsGetParametersQueryAssetType.IMG,
        pageNo,
        pageSize,
      });
      return {
        data: res?.data ?? [],
        pageNo: res?.pageNo ?? pageNo,
        pageSize: res?.pageSize ?? pageSize,
        total: res?.total ?? 0,
      };
    },
  });

  const handlePreview = useCallback((src: string) => {
    if (!src) return;
    setPreviewSrc(src);
    setPreviewOpen(true);
  }, []);

  const handleDelete = useCallback(
    async (record: UserAssetRecord) => {
      if (record.id === undefined || record.id === null) return;
      if (deletingId === record.id) return;
      setDeletingId(record.id);
      try {
        await libraryService.fetchTrash(String(record.id));
        refresh();
      } catch {
        message.error(t("Failed"));
      } finally {
        setDeletingId(null);
      }
    },
    [deletingId, refresh],
  );

  useEffect(() => {
    window.addEventListener(LIBRARY_IMAGE_ASSETS_REFRESH_EVENT, refresh);
    return () => {
      window.removeEventListener(LIBRARY_IMAGE_ASSETS_REFRESH_EVENT, refresh);
    };
  }, [refresh]);

  const status: Status = useMemo(() => {
    if (loading) return "loading";
    if (error) return "failed";
    if (items.length === 0) return "blank";
    return "succeeded";
  }, [loading, error, items.length]);

  return (
    <>
      <Section className="min-h-[300px]" status={status} reload={refresh}>
        <div className="grid grid-cols-5 min-[1920px]:grid-cols-6 gap-[16px]">
          {items.map((item) => (
            <UIImageCard key={item.id} record={item} onPreview={handlePreview} onDelete={handleDelete} />
          ))}
        </div>

        <div ref={sentinelRef} className="h-[24px]" />

        {loadingMore && (
          <div className="flex justify-center py-[8px]">
            <Spin size="small" />
          </div>
        )}

        {!hasMore && items.length > 0 && (
          <div className="py-[8px] text-center text-[12px] text-kds-fg-soft">
            <Trans>No more items</Trans>
          </div>
        )}
      </Section>

      <AntImage
        src={previewSrc}
        styles={{ root: { display: "none" } }}
        preview={{
          visible: previewOpen,
          onVisibleChange: setPreviewOpen,
        }}
      />
    </>
  );
};

export default UIImageList;
